import type { ContainerResult, SearchResults } from '@peruselab/api-client/moleculeSearch';

function filterAndDedupe(
  structureContainers: readonly ContainerResult[],
  textContainers: readonly ContainerResult[],
  moleculeIds: ReadonlySet<string>
): ContainerResult[] {
  const byDocumentId = new Map<string, ContainerResult>();
  // Structure results are added first and never overwritten below, so their category/score
  // (the scale every downstream consumer -- filter checkboxes, min-score slider, sort -- is
  // built around) always wins over text's repurposed IDENTICAL/ES-relevance-score pair.
  for (const result of structureContainers) {
    if (result.sourceMoleculeId && moleculeIds.has(result.sourceMoleculeId))
      byDocumentId.set(result.documentId, result);
  }
  for (const result of textContainers) {
    if (result.sourceMoleculeId && moleculeIds.has(result.sourceMoleculeId) && !byDocumentId.has(result.documentId)) {
      byDocumentId.set(result.documentId, result);
    }
  }
  const direct = new Set(textContainers.filter((result) => result.textMatch).map((result) => result.documentId));
  for (const result of structureContainers)
    if (direct.has(result.documentId))
      byDocumentId.set(result.documentId, { ...result, textMatch: true, hasAnnotations: true });
  return [...byDocumentId.values()];
}

/**
 * Text+Drawing mode (#463): a molecule must match *both* the typed text query and the current
 * canvas structure. Requires both a non-empty canvas and a non-blank query -- "both must match"
 * has no meaning with only one side supplied, so this never silently degrades to a single-sided
 * search when the other side is empty/idle.
 */
export function intersectSearchResults(
  structureResults: SearchResults | null,
  textResults: SearchResults | null,
  hasCanvasStructure: boolean,
  hasTextQuery: boolean
): SearchResults | null {
  if (!hasCanvasStructure || !hasTextQuery || !structureResults || !textResults) return null;

  const textMoleculeIds = new Set(textResults.molecules.map((result) => result.moleculeId));
  const intersectedMolecules = structureResults.molecules.filter((result) => textMoleculeIds.has(result.moleculeId));
  const moleculeIds = new Set(intersectedMolecules.map((result) => result.moleculeId));

  return {
    molecules: intersectedMolecules,
    drawings: filterAndDedupe(structureResults.drawings, textResults.drawings, moleculeIds),
    reactions: filterAndDedupe(structureResults.reactions, textResults.reactions, moleculeIds),
    illustrations: filterAndDedupe(structureResults.illustrations, textResults.illustrations, moleculeIds)
  };
}
